import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Certificate as CertIcon, ShareNetwork, Leaf } from "@phosphor-icons/react";
import { custApi, CUST_API_BASE, CUST_TOKEN_KEY, formatErr } from "../../lib/customerApi";

export default function MeCertificates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    custApi.get("/me/certificates")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const download = async (c) => {
    try {
      const token = localStorage.getItem(CUST_TOKEN_KEY);
      const r = await fetch(`${CUST_API_BASE}/me/certificates/${c.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${c.cert_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const share = async (c) => {
    const text = `I've saved ${c.total_co2_kg} kg of CO₂ through Rescigo Health! 🌿 Certificate ${c.cert_no}`;
    if (navigator.share) {
      try { await navigator.share({ title: "My Rescigo Impact", text }); } catch { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
      catch { toast.error("Unable to share"); }
    }
  };

  const totalCO2 = items.reduce((s, c) => s + (c.total_co2_kg || 0), 0);

  return (
    <div className="space-y-5 px-4 py-4" data-testid="me-certs">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Certificates</div>
        <h1 className="font-heading text-xl text-[#1A1A1A]">Your green impact</h1>
      </div>

      {/* Impact hero */}
      <div className="rounded-xl bg-[#DC2626] text-white p-5 relative overflow-hidden" data-testid="me-cert-hero">
        <div className="absolute -right-5 -top-5 opacity-10"><Leaf size={150} weight="fill" /></div>
        <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Total CO₂ saved (certified)</div>
        <div className="font-heading text-4xl mt-1">{totalCO2.toFixed(1)} <span className="text-xl opacity-80">kg</span></div>
        <div className="text-xs opacity-80 mt-1">{items.length} certificate{items.length === 1 ? "" : "s"} issued</div>
      </div>

      {loading && <div className="text-xs text-[#8A8780]">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="bg-white border border-[#E5E2D9] rounded-lg p-8 text-center">
          <CertIcon size={30} className="inline-block mb-3 opacity-40" />
          <div className="text-sm text-[#5C5A55]">No certificates yet</div>
          <div className="text-xs text-[#8A8780] mt-1">Keep segregating — your first certificate will appear here soon.</div>
        </div>
      )}

      <div className="space-y-3" data-testid="me-cert-list">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-[#E5E2D9] rounded-lg p-5" data-testid={`me-cert-${c.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-md bg-[#FEE2E2] flex items-center justify-center">
                <CertIcon size={18} color="#DC2626" weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono font-medium text-sm">{c.cert_no}</div>
                <div className="text-[11px] text-[#5C5A55] mt-0.5">
                  {c.period_start} → {c.period_end}
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <Stat label="Waste" value={`${c.total_kg} kg`} />
                  <Stat label="CO₂ saved" value={`${c.total_co2_kg} kg`} accent />
                  <Stat label="Bags" value={c.total_bags} />
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                data-testid={`me-cert-pdf-${c.id}`}
                onClick={() => download(c)}
                className="flex-1 h-10 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-medium inline-flex items-center justify-center gap-1.5"
              ><Download size={12} weight="bold" />Download PDF</button>
              <button
                data-testid={`me-cert-share-${c.id}`}
                onClick={() => share(c)}
                className="h-10 px-4 border border-[#E5E2D9] hover:bg-[#F7F6F2] rounded-md text-xs font-medium inline-flex items-center justify-center gap-1.5"
              ><ShareNetwork size={12} weight="bold" />Share</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div>
    <div className="text-[9px] uppercase tracking-[0.16em] text-[#8A8780]">{label}</div>
    <div className={`font-heading text-lg leading-tight ${accent ? "text-[#DC2626]" : "text-[#1A1A1A]"}`}>{value}</div>
  </div>
);
